/*
  swatch-colors.js — shared color-name → hex map for filter swatches.

  Why: the filter "color circle" markup sets its background to the raw color
  NAME used as a CSS color (e.g. background: forest-green). CSS only knows
  ~148 named colors, so apparel names like "Forest Green", "Athletic Heather",
  "Sport Grey" render as an empty/transparent dot. This module is the single
  code-side source of truth that fills those dots in bulk.

  Three render paths consume it, all via window.ArtemisSwatch.paint():
    - sections/filter-group.liquid   (server-rendered, e.g. search filters)
    - assets/collection-filters.js   (client faceting)
    - assets/collection-filters-api.js (backend faceting service)

  Merchant overrides: any <script type="application/json" data-swatch-map> in
  the page (built from the section's "Swatch color map" setting) is merged ON
  TOP of this map, so a merchant can correct/extend a color without code.
*/
(function () {
  'use strict';

  // Canonical apparel palette. Keys are normalized (lowercase, single-spaced).
  // Hexes approximate common SanMar / Gildan / Port & Company / Sport-Tek /
  // District / Next Level swatches — close enough for a filter dot.
  var MAP = {
    // Neutrals
    'black': '#1a1a1a',
    'jet black': '#0f0f0f',
    'white': '#ffffff',
    'natural': '#efe7d3',
    'ivory': '#f4efe1',
    'cream': '#f3ecd8',
    'bone': '#e7ddca',
    'beige': '#d9c8a9',
    'sand': '#d8c9a3',
    'stone': '#cabfa6',
    'khaki': '#bda878',
    'tan': '#c9a96b',
    'silver': '#c7c9c8',
    'ash': '#cdccc4',
    'ash grey': '#cdccc4',
    'ash gray': '#cdccc4',
    'sport grey': '#b3b4b2',
    'sport gray': '#b3b4b2',
    'athletic heather': '#b7b7b3',
    'heather grey': '#b3b3b3',
    'heather gray': '#b3b3b3',
    'grey': '#9a9a9a',
    'gray': '#9a9a9a',
    'light grey': '#cfcfcf',
    'light gray': '#cfcfcf',
    'medium grey': '#8a8d8f',
    'medium gray': '#8a8d8f',
    'dark grey': '#5a5a5a',
    'dark gray': '#5a5a5a',
    'dark heather': '#46474a',
    'graphite': '#4a4d50',
    'graphite heather': '#52555a',
    'charcoal': '#3d3f43',
    'smoke': '#73726e',
    'storm': '#6f7679',
    'slate': '#54585c',
    'steel': '#5b6770',

    // Blues
    'navy': '#1b2a4a',
    'true navy': '#1d2d50',
    'midnight navy': '#16213c',
    'heather navy': '#2a3654',
    'royal': '#1d4ed8',
    'royal blue': '#1d4ed8',
    'true royal': '#1f50d6',
    'cobalt': '#1c43c2',
    'electric blue': '#2563eb',
    'indigo': '#3f3f9e',
    'denim': '#4a6a8f',
    'slate blue': '#5a7392',
    'steel blue': '#4a708f',
    'carolina blue': '#7ba8d6',
    'columbia blue': '#8fb8de',
    'light blue': '#a7c8e6',
    'sky': '#87c2e8',
    'sky blue': '#87c2e8',
    'powder blue': '#aacbe2',
    'aqua': '#4fc3d4',
    'turquoise': '#34b6c4',
    'teal': '#1f8a8f',

    // Greens
    'green': '#00843d',
    'kelly': '#1f9e4a',
    'kelly green': '#1f9e4a',
    'irish green': '#2faa4a',
    'forest': '#1f4d2e',
    'forest green': '#1f4d2e',
    'dark green': '#1d4429',
    'hunter': '#284e36',
    'hunter green': '#284e36',
    'olive': '#6b6a3a',
    'military green': '#4f5234',
    'army': '#4b5320',
    'lime': '#9ccb3b',
    'neon green': '#7ed957',
    'mint': '#a8e0c0',
    'seafoam': '#9fd9bf',
    'jade': '#2e9e7a',

    // Reds / warm
    'red': '#c8102e',
    'true red': '#c8102e',
    'cardinal': '#9b1c2e',
    'brick': '#9c4a3a',
    'maroon': '#6f263d',
    'burgundy': '#5c1a2b',
    'wine': '#5a1f2d',
    'cardinal red': '#9b1c2e',
    'scarlet': '#c41e3a',
    'heather red': '#c0394a',

    // Oranges / yellows / golds
    'orange': '#ff6900',
    'burnt orange': '#bf5700',
    'texas orange': '#bf5700',
    'rust': '#a85b2a',
    'terracotta': '#b15a3a',
    'coral': '#ff7f6b',
    'salmon': '#fa8072',
    'peach': '#ffcaa8',
    'gold': '#ffb81c',
    'old gold': '#d2a629',
    'vegas gold': '#c5b358',
    'antique gold': '#cda434',
    'athletic gold': '#ffc20e',
    'mustard': '#d4a017',
    'yellow': '#ffd400',
    'daisy': '#ffdf2b',
    'lemon': '#fff04d',

    // Pinks / purples
    'pink': '#f4a6c0',
    'light pink': '#f7c5d6',
    'hot pink': '#e3007f',
    'neon pink': '#ff5fa2',
    'fuchsia': '#cf2d7a',
    'raspberry': '#b3315f',
    'rose': '#c76b80',
    'mauve': '#b289a0',
    'lavender': '#c3b3e0',
    'violet': '#7a4fbf',
    'purple': '#5f259f',
    'plum': '#5d3a5a',
    'eggplant': '#43314a',

    // Browns
    'brown': '#5a3a25',
    'chocolate': '#4a2f1f',
    'dark chocolate': '#3a241a',
    'coffee': '#4b3621',

    // Safety / hi-vis
    'safety green': '#c4e000',
    'safety yellow': '#e6e000',
    'safety orange': '#ff5c00',
    'neon yellow': '#eaff00'
  };

  function normalize(s) {
    return String(s == null ? '' : s).trim().toLowerCase().replace(/\s+/g, ' ');
  }

  // Common qualifiers that don't change the base hue enough to matter for a dot.
  var QUALIFIERS = /\b(heather|heathered|antique|athletic|sport|vintage|classic|true|light|dark|deep|bright|neon|safety|solid)\b/g;

  function hex(name) {
    var key = normalize(name);
    if (!key) return null;
    if (MAP[key]) return MAP[key];

    // Try stripping qualifiers: "Heather Forest Green" -> "forest green".
    var stripped = key.replace(QUALIFIERS, '').trim().replace(/\s+/g, ' ');
    if (stripped && stripped !== key && MAP[stripped]) return MAP[stripped];

    // Fall back to the base color word: "Forest Green" -> "green".
    var parts = key.split(' ');
    if (parts.length > 1) {
      var last = parts[parts.length - 1];
      if (MAP[last]) return MAP[last];
    }
    return null;
  }

  // Pull merchant overrides from any data-swatch-map JSON already in the DOM.
  function loadOverrides() {
    var els = document.querySelectorAll('[data-swatch-map]');
    for (var i = 0; i < els.length; i++) {
      try {
        var obj = JSON.parse(els[i].textContent || '{}');
        for (var k in obj) {
          if (obj.hasOwnProperty(k) && obj[k]) MAP[normalize(k)] = obj[k];
        }
      } catch (e) { /* ignore malformed override blocks */ }
    }
  }

  // Fill every .filter-swatch__dot under root whose color we can resolve.
  function paint(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var dots = scope.querySelectorAll('.filter-swatch__dot');
    for (var i = 0; i < dots.length; i++) {
      var dot = dots[i];
      // A dot locked with an authoritative hex (e.g. from the faceting index)
      // is left as-is; the name→hex map is only a fallback for the rest.
      if (dot.hasAttribute('data-swatch-locked')) continue;
      var name = dot.getAttribute('data-swatch-name');
      if (!name) {
        var label = dot.parentNode && dot.parentNode.querySelector('.filter-swatch__name');
        name = label ? label.textContent : '';
      }
      var h = hex(name);
      if (h) dot.style.background = h;
    }
  }

  loadOverrides();
  window.ArtemisSwatch = { hex: hex, paint: paint, map: MAP, normalize: normalize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { paint(document); });
  } else {
    paint(document);
  }
})();
